import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { submitForm } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { FadeIn, Kicker, SectionShell } from "./Reveal";

const inputCls =
  "w-full border border-border bg-background px-4 py-3 text-sm transition-colors placeholder:text-muted-foreground/60";

export default function JoinSection() {
  const { d } = useI18n();
  const j = d.join;
  const [form, setForm] = useState({ name: "", email: "", country: "", region: "", organization: "", category: "", message: "" });
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await submitForm({ type: "join", ...form, privacy_consent: privacy });
      setDone(res.data);
      toast.success(j.toastOk);
    } catch {
      toast.error(j.toastErr);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionShell id="join" className="border-t border-border bg-card/40">
      <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr]">
        <FadeIn>
          <Kicker>{j.kicker}</Kicker>
          <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {j.titleA}<span className="italic text-crimson dark:text-stalk">{j.titleAccent}</span>{j.titleB}
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">{j.para}</p>
          <ul className="mt-8 space-y-3 text-sm text-foreground/80">
            {j.bullets.map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-crimson dark:bg-stalk" />
                {t}
              </li>
            ))}
          </ul>
        </FadeIn>

        <FadeIn delay={0.15}>
          {done ? (
            <div className="corner-ticks flex h-full flex-col items-start justify-center border border-border bg-background p-10" data-testid="join-form-success">
              <CheckCircle2 size={36} className="text-foliage dark:text-stalk" />
              <h3 className="mt-6 font-serif text-2xl">{j.thanks}, {done.name.split(" ")[0]}.</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{j.success}</p>
              <p className="mt-6 border border-border px-4 py-2 font-mono text-xs tracking-[0.2em] text-crimson dark:text-stalk" data-testid="join-form-reference">
                REF · {done.reference}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="corner-ticks border border-border bg-background p-8 sm:p-10" data-testid="join-form">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{j.formTitle}</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <input required value={form.name} onChange={set("name")} placeholder={j.ph.name} className={inputCls} data-testid="join-form-name-input" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder={j.ph.email} className={inputCls} data-testid="join-form-email-input" />
                <input value={form.country} onChange={set("country")} placeholder={j.ph.country} className={inputCls} data-testid="join-form-country-input" />
                <input value={form.region} onChange={set("region")} placeholder={j.ph.region} className={inputCls} data-testid="join-form-region-input" />
                <input value={form.organization} onChange={set("organization")} placeholder={j.ph.org} className={`${inputCls} sm:col-span-2`} data-testid="join-form-organization-input" />
                <select required value={form.category} onChange={set("category")} className={`${inputCls} sm:col-span-2`} data-testid="join-form-category-select">
                  <option value="">{j.categoryPh}</option>
                  {j.categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <textarea value={form.message} onChange={set("message")} placeholder={j.ph.message} rows={4} className={`${inputCls} resize-none sm:col-span-2`} data-testid="join-form-message-input" />
              </div>
              <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-foreground/80">
                <input required type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-1 h-4 w-4 accent-crimson" data-testid="join-form-privacy-checkbox" />
                <span>
                  {j.privacy} <Link to="/privacy" data-testid="join-form-privacy-link" className="u-link text-crimson dark:text-stalk">{j.privacyLink}</Link>{j.privacySuffix}
                </span>
              </label>
              <button
                type="submit"
                disabled={busy || !privacy}
                data-testid="join-form-submit-button"
                className="group mt-6 flex w-full items-center justify-center gap-3 border border-crimson bg-crimson px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] text-parchment transition-colors hover:bg-transparent hover:text-crimson disabled:opacity-60"
              >
                {busy ? j.submitting : j.submit}
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{j.note}</p>
            </form>
          )}
        </FadeIn>
      </div>
    </SectionShell>
  );
}
